import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  api,
  canReceiveBookings,
  type CalendarInfo,
  type ConferenceType,
  type Connection,
  type Link,
  type Me,
  type Weekday,
  type WeeklyHours,
} from '../api';
import { fmtMinutes } from '../api';
import { HOST_TZ, TimezoneSelect } from '../components/TimezoneSelect';
import { ErrorBox, Loading } from '../components/bk';
import { useMe } from '../../App';

const DAYS: [Weekday, string][] = [
  ['mon', 'Mon'],
  ['tue', 'Tue'],
  ['wed', 'Wed'],
  ['thu', 'Thu'],
  ['fri', 'Fri'],
  ['sat', 'Sat'],
  ['sun', 'Sun'],
];
const NINE_TO_FIVE = { start: '09:00', end: '17:00' };
const DURATION_CHOICES = [15, 20, 30, 45, 60, 90, 120];
const REMINDER_CHOICES: [number, string][] = [
  [1440, '24 hours before'],
  [120, '2 hours before'],
  [60, '1 hour before'],
  [15, '15 minutes before'],
];
const DEFAULT_REMINDERS = [1440, 60];

interface Form {
  slug: string;
  title: string;
  description: string;
  durationMinutes: number;
  durationOptions: number[];
  reminderMinutes: number[];
  slotIntervalMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  minNoticeMinutes: number;
  maxDaysAhead: number;
  timezone: string;
  weeklyHours: WeeklyHours;
  hostEmails: string[];
  destination: { connectionId: string; calendarId: string };
  conferenceType: ConferenceType;
  active: boolean;
}

const blank = (tz: string): Form => ({
  slug: '',
  title: '',
  description: '',
  durationMinutes: 30,
  durationOptions: [30],
  reminderMinutes: DEFAULT_REMINDERS,
  slotIntervalMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  minNoticeMinutes: 240,
  maxDaysAhead: 60,
  timezone: tz,
  weeklyHours: Object.fromEntries(DAYS.slice(0, 5).map(([d]) => [d, [NINE_TO_FIVE]])),
  hostEmails: [],
  destination: { connectionId: '', calendarId: '' },
  conferenceType: 'teams',
  active: true,
});

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

export function LinkEditor() {
  const { slug } = useParams();
  const isNew = !slug;
  const me = useOutletContext<Me | null>();
  const nav = useNavigate();
  const [form, setForm] = useState<Form | null>(isNew ? blank(me?.timezone ?? HOST_TZ) : null);
  // New links follow the host's own time zone once the profile loads (Chicago unless they changed it).
  useEffect(() => {
    if (isNew && me?.timezone) setForm((f) => (f ? { ...f, timezone: me.timezone } : f));
  }, [isNew, me?.timezone]);
  const [conns, setConns] = useState<Connection[]>([]);
  const [calendars, setCalendars] = useState<CalendarInfo[]>([]);
  const [hostInput, setHostInput] = useState('');
  const [colleagues, setColleagues] = useState<{ email: string; name: string }[]>([]);
  const plan = useMe().company?.plan;
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    api.get<Connection[]>('/api/connections').then(setConns).catch((e) => setError(e.message));
    api.get<{ email: string; name: string }[]>('/api/colleagues').then(setColleagues).catch(() => undefined);
    if (!isNew)
      api
        .get<Link>(`/api/links/${slug}`)
        .then((l) =>
          setForm({
            ...blank(l.timezone),
            ...l,
            durationOptions: l.durationOptions?.length ? l.durationOptions : [l.durationMinutes],
            reminderMinutes: l.reminderMinutes ?? DEFAULT_REMINDERS,
            description: l.description ?? '',
            hostEmails: l.hosts.filter((h) => h.sub !== l.ownerSub).map((h) => h.email),
          }),
        )
        .catch((e) => setError(e.message));
  }, [slug, isNew]);

  // Default destination to the first calendar connection.
  useEffect(() => {
    if (form && !form.destination.connectionId) {
      const first = conns.find(canReceiveBookings);
      if (first) setForm({ ...form, destination: { connectionId: first.id, calendarId: '' } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conns]);

  const connectionId = form?.destination.connectionId;
  useEffect(() => {
    if (!connectionId) return setCalendars([]);
    api
      .get<CalendarInfo[]>(`/api/connections/${connectionId}/calendars`)
      .then((cals) => {
        const writable = cals.filter((c) => c.canWrite);
        setCalendars(writable);
        setForm((f) =>
          f && !writable.some((c) => c.id === f.destination.calendarId)
            ? { ...f, destination: { ...f.destination, calendarId: (writable.find((c) => c.primary) ?? writable[0])?.id ?? '' } }
            : f,
        );
      })
      .catch((e) => setError(e.message));
  }, [connectionId]);

  if (!form) return error ? <ErrorBox error={error} /> : <Loading />;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm({ ...form, [k]: v });
  const calendarConns = conns.filter(canReceiveBookings);

  const setRange = (d: Weekday, i: number, key: 'start' | 'end', v: string) => {
    const ranges = [...(form.weeklyHours[d] ?? [])];
    ranges[i] = { ...ranges[i], [key]: v };
    set('weeklyHours', { ...form.weeklyHours, [d]: ranges });
  };
  const toggleDay = (d: Weekday, on: boolean) => {
    const wh = { ...form.weeklyHours };
    if (on) wh[d] = [NINE_TO_FIVE];
    else delete wh[d];
    set('weeklyHours', wh);
  };

  const addHost = () => {
    const email = hostInput.trim().toLowerCase();
    if (!email || form.hostEmails.includes(email)) return;
    setError('');
    set('hostEmails', [...form.hostEmails, email]);
    setHostInput('');
  };
  const nameOf = (email: string) => colleagues.find((c) => c.email === email)?.name ?? email;
  const canAddHosts = Boolean(plan?.groupLinks);
  const advanced = plan?.advanced !== false;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (isNew) await api.post('/api/links', form);
      else await api.put(`/api/links/${slug}`, form);
      nav('/links');
    } catch (e) {
      setError((e as Error).message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this link? Existing meetings stay on the calendars.')) return;
    await api.del(`/api/links/${slug}`).then(() => nav('/links')).catch((e) => setError(e.message));
  };

  return (
    <>
      <div className="pagehead">
        <h1>{isNew ? 'New booking link' : 'Edit link'}</h1>
      </div>
      <ErrorBox error={error} />

      {!calendarConns.length && (
        <div className="alert">Connect a Google or Outlook calendar first (Calendars tab), so meetings have somewhere to go.</div>
      )}

      <section className="card">
        <h2>Basics</h2>
        <div className="field">
          <label htmlFor="title">Title</label>
          <input id="title" type="text" value={form.title} placeholder="Intro call"
            onChange={(e) => setForm({ ...form, title: e.target.value, slug: isNew && !slugTouched ? slugify(`${me?.name ?? ''} ${e.target.value}`) : form.slug })} />
        </div>
        <div className="field">
          <label htmlFor="slug">Link</label>
          <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
            <span className="muted small" style={{ whiteSpace: 'nowrap' }}>{location.host}/b/</span>
            <input id="slug" type="text" value={form.slug} disabled={!isNew}
              onChange={(e) => (setSlugTouched(true), set('slug', slugify(e.target.value)))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="desc">Description</label>
          <textarea id="desc" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Shown on the booking page" />
        </div>
        <div className="field">
          <label id="dur-label">Duration</label>
          <div className="chips" role="group" aria-labelledby="dur-label">
            {DURATION_CHOICES.map((m) => {
              const on = form.durationOptions.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  className={`chip ${on ? 'on' : ''}`}
                  aria-pressed={on}
                  onClick={() => {
                    const next = on ? form.durationOptions.filter((d) => d !== m) : [...form.durationOptions, m];
                    if (!next.length) return; // a link needs at least one length
                    next.sort((a, b) => a - b);
                    setForm({ ...form, durationOptions: next, durationMinutes: next[0], slotIntervalMinutes: Math.min(next[0], 30) });
                  }}
                >
                  {fmtMinutes(m)}
                </button>
              );
            })}
          </div>
          <div className="hint">
            {!advanced && 'The Free plan offers one length per link. '}
            {form.durationOptions.length > 1
              ? `Clients choose: ${form.durationOptions.map(fmtMinutes).join(', ')}. Open times are worked out for the length they pick.`
              : 'Pick more than one to let clients choose how long they need.'}
          </div>
        </div>
        <div className="two">
          <div className="field">
            <label htmlFor="conf">Meeting type</label>
            <select id="conf" value={form.conferenceType} onChange={(e) => set('conferenceType', e.target.value as ConferenceType)}>
              <option value="teams">Microsoft Teams</option>
              <option value="webex">Webex (personal room)</option>
              <option value="phone">Conference call (dial-in)</option>
            </select>
          </div>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Link is on and accepting bookings
        </label>
      </section>

      <section className="card">
        <h2>Where meetings go</h2>
        <div className="two">
          <div className="field">
            <label htmlFor="dconn">Calendar account</label>
            <select id="dconn" value={form.destination.connectionId} onChange={(e) => set('destination', { connectionId: e.target.value, calendarId: '' })}>
              <option value="">Choose…</option>
              {calendarConns.map((c) => (
                <option key={c.id} value={c.id}>{c.provider === 'google' ? 'Google' : 'Outlook'} · {c.accountEmail}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="dcal">Calendar</label>
            <select id="dcal" value={form.destination.calendarId} onChange={(e) => set('destination', { ...form.destination, calendarId: e.target.value })}>
              {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}{c.primary ? ' (primary)' : ''}</option>)}
            </select>
          </div>
        </div>
        <p className="hint">The invitee and co-hosts get the invite from this calendar. Busy time from all your connected calendars is always excluded.</p>
      </section>

      <section className="card">
        <h2>Co-hosts</h2>
        {!canAddHosts ? (
          <div className="alert info">Group links with co-hosts are part of the Teams plan. An admin can upgrade under Company → Billing.</div>
        ) : (
          <>
            <p className="muted small">Add colleagues to offer only times when everyone is free. They need to have signed in and connected a calendar.</p>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <select aria-label="Colleague" value={hostInput} onChange={(e) => setHostInput(e.target.value)}>
                <option value="">Choose a colleague…</option>
                {colleagues.filter((c) => !form.hostEmails.includes(c.email)).map((c) => (
                  <option key={c.email} value={c.email}>{c.name}{c.name !== c.email ? ` · ${c.email}` : ''}</option>
                ))}
              </select>
              <button onClick={addHost} disabled={!hostInput}>Add</button>
            </div>
          </>
        )}
        <ul className="list">
          {form.hostEmails.map((h) => (
            <li key={h} className="row between">
              <span>{nameOf(h)}</span>
              <button className="link-btn" onClick={() => set('hostEmails', form.hostEmails.filter((x) => x !== h))}>Remove</button>
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>Available hours</h2>
        <div className="field">
          <label htmlFor="tz">Time zone</label>
          <TimezoneSelect id="tz" value={form.timezone} onChange={(tz) => set('timezone', tz)} />
          <div className="hint">The hours below are in this time zone. Clients see them converted to their own local time.</div>
        </div>
        {DAYS.map(([d, label]) => {
          const ranges = form.weeklyHours[d] ?? [];
          return (
            <div className="hours-row" key={d}>
              <label className="check"><input type="checkbox" checked={ranges.length > 0} onChange={(e) => toggleDay(d, e.target.checked)} />{label}</label>
              <div>
                {!ranges.length && <span className="muted small">Unavailable</span>}
                {ranges.map((r, i) => (
                  <div className="range" key={i}>
                    <input type="time" value={r.start} onChange={(e) => setRange(d, i, 'start', e.target.value)} aria-label={`${label} start`} />
                    <span>–</span>
                    <input type="time" value={r.end} onChange={(e) => setRange(d, i, 'end', e.target.value)} aria-label={`${label} end`} />
                    <button className="link-btn" aria-label="Remove range" onClick={() => set('weeklyHours', { ...form.weeklyHours, [d]: ranges.filter((_, j) => j !== i) })}>✕</button>
                  </div>
                ))}
                {ranges.length > 0 && (
                  <button className="link-btn small" onClick={() => set('weeklyHours', { ...form.weeklyHours, [d]: [...ranges, { start: '13:00', end: '17:00' }] })}>+ Add hours</button>
                )}
              </div>
            </div>
          );
        })}
      </section>

      <section className="card">
        <h2>Reminders</h2>
        {!advanced && <div className="alert info">Reminder emails are part of the Standard and Teams plans.</div>}
        <label className="check" style={{ marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={form.reminderMinutes.length > 0}
            onChange={(e) => set('reminderMinutes', e.target.checked ? DEFAULT_REMINDERS : [])}
          />
          Email the client a reminder before the meeting
        </label>
        {form.reminderMinutes.length > 0 && (
          <div className="chips" role="group" aria-label="When to send reminders">
            {REMINDER_CHOICES.map(([m, label]) => {
              const on = form.reminderMinutes.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  className={`chip ${on ? 'on' : ''}`}
                  aria-pressed={on}
                  onClick={() => {
                    const next = on ? form.reminderMinutes.filter((x) => x !== m) : [...form.reminderMinutes, m];
                    set('reminderMinutes', next.sort((a, b) => b - a));
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        <p className="hint">
          Reminders go to the client and any guests they added, with the meeting time in their time zone and the join link. They
          move automatically if the meeting is rescheduled, and stop if it's canceled. Reminders that would fall in the past
          (for example a 24-hour reminder on a same-day booking) are skipped.
        </p>
      </section>

      <section className="card">
        <h2>Scheduling rules</h2>
        <div className="two">
          <NumberField label="Start times every (min)" value={form.slotIntervalMinutes} onChange={(v) => set('slotIntervalMinutes', v)} />
          <NumberField label="Minimum notice (hours)" value={form.minNoticeMinutes / 60} onChange={(v) => set('minNoticeMinutes', Math.round(v * 60))} />
          <NumberField label="Buffer before (min)" value={form.bufferBeforeMinutes} onChange={(v) => set('bufferBeforeMinutes', v)} />
          <NumberField label="Buffer after (min)" value={form.bufferAfterMinutes} onChange={(v) => set('bufferAfterMinutes', v)} />
          <NumberField label="Bookable days ahead" value={form.maxDaysAhead} onChange={(v) => set('maxDaysAhead', v)} />
        </div>
      </section>

      <div className="row between">
        {!isNew ? <button className="danger" onClick={remove}>Delete link</button> : <span />}
        <div className="row">
          <button onClick={() => nav('/links')}>Cancel</button>
          <button className="primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save link'}</button>
        </div>
      </div>
    </>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type="number" min={0} inputMode="numeric" value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}
